/**
 * In-memory cache of decrypted family private keys, keyed by orgId.
 *
 * Populated by SafeContext after login/unlock: for each org that has a
 * `familyKeyWrap` bundle, the family private key is decrypted from that
 * ECIES bundle using the user's account keypair (`decryptFamilyHandoff`
 * from `@heybesafe/shared`) and cached here. Cleared on logout/lock.
 *
 * Consumers (e.g. syncPull) call `getKey(orgId)` to retrieve the
 * plaintext family private key needed to decrypt shared vault items.
 */

import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

interface FamilyKeysContextValue {
  addKey(orgId: string, privateKey: Uint8Array): void;
  /**
   * Returns the cached family private key for the given orgId, or undefined.
   *
   * The returned `Uint8Array` is a reference to the cached copy — DO NOT
   * mutate it. Mutating it corrupts the cache for all subsequent reads in
   * the same session.
   */
  getKey(orgId: string): Uint8Array | undefined;
  clear(): void;
  /** Increments whenever the cache changes (a key is added or the cache is
   *  cleared). Consumers that need to react to key *hydration* — which happens
   *  asynchronously after unlock — depend on this; `getKey` alone is a stable
   *  reference and won't trigger a re-run when a key lands. */
  version: number;
}

const FamilyKeysContext = createContext<FamilyKeysContextValue | null>(null);

export function FamilyKeysProvider({ children }: { children: ReactNode }) {
  // Bump a version state when contents change so consumers re-render.
  // The actual map is in a ref so updates don't leak intermediate state.
  const mapRef = useRef<Map<string, Uint8Array>>(new Map());
  const [version, setVersion] = useState(0);

  const addKey = useCallback((orgId: string, privateKey: Uint8Array) => {
    mapRef.current.set(orgId, privateKey);
    setVersion((v) => v + 1);
  }, []);

  const getKey = useCallback((orgId: string) => mapRef.current.get(orgId), []);

  const clear = useCallback(() => {
    mapRef.current.clear();
    setVersion((v) => v + 1);
  }, []);

  const value = useMemo(() => ({ addKey, getKey, clear, version }), [addKey, getKey, clear, version]);

  return <FamilyKeysContext.Provider value={value}>{children}</FamilyKeysContext.Provider>;
}

export function useFamilyKeys(): FamilyKeysContextValue {
  const ctx = useContext(FamilyKeysContext);
  if (!ctx) {
    throw new Error('useFamilyKeys must be called inside a FamilyKeysProvider');
  }
  return ctx;
}
